import { CHART_TYPES } from '../../constants/chatbot-expand.constants';

/**
 * TransposeHideNaHelper
 *
 * Extracted from ChatbotexpandComponent. Holds the transpose toggle, the "Hide NA"
 * toggle and the NA-filtering utilities that belong together.
 *
 * Follows the existing `comp: any` helper pattern — the component keeps thin wrapper
 * methods (bound in the template) that delegate here, so all call sites remain unchanged
 * and behavior is preserved.
 */
export class TransposeHideNaHelper {
  static OnTransposeClick(comp: any, block: any = null): void {
    const target = block || comp;

    // If Hide NA is active, we should re-filter based on the new transpose state!
    if (target.isHideNA) {
      let filteredData = [...(target.CrossTabOutPutEntireData || [])];
      if (!target.isTotal) {
        filteredData = filteredData.filter((x: any) => !x.isTotal);
      }
      if (target.isIndex && target.selectedIndex !== '') {
        target.CrossTabOutPutData = filteredData;
        comp.manipulateIndexData(block);
        filteredData = [...(target.CrossTabOutPutData || [])];
      }
      // Toggle transpose
      target.isTranspose = !target.isTranspose;
      filteredData = comp.getHideNAFilteredData(filteredData, !!target.isTranspose, block);
      target.CrossTabOutPutData = filteredData;
      target.OutputdataObject = { ...(target.OutputdataObject || {}), Row: [], Column: [] } as any;
      comp.BindTableOutput(target.CrossTabOutPutData, block);
      if (target.isTranspose) {
        comp.handleOutputdataObjectOnTransposeSelect(block);
      }
    } else {
      comp.handleOutputdataObjectOnTransposeSelect(block);
      target.isTranspose = !target.isTranspose;
    }

    comp.updateChartDisablingFlags(block);

    // Fallback if chart view is disabled or all charts are disabled
    if (!target.isChartView) {
      target.isTableView = true;
      target.isVerticalBarChartClick = false;
      target.isVerticalGroupBarChartClick = false;
      target.isVerticalStackChartClick = false;
      target.isLineChartClick = false;
    } else {
      // Fallback if the currently selected chart becomes disabled
      if (target.isVerticalStackChartClick && target.disableStackChart) {
        target.isVerticalStackChartClick = false;
        target.isVerticalBarChartClick = true;
      }
      if (target.isLineChartClick && target.disableLineChart) {
        target.isLineChartClick = false;
        target.isVerticalBarChartClick = true;
      }

      if (
        (target.isVerticalGroupBarChartClick || target.isVerticalBarChartClick) &&
        target.isTranspose
      ) {
        comp.VerticalChartClick(CHART_TYPES.BAR, block);
      } else if (
        (target.isVerticalGroupBarChartClick || target.isVerticalBarChartClick) &&
        !target.isTranspose
      ) {
        comp.VerticalChartClick(CHART_TYPES.COLUMN, block);
      } else if (target.isVerticalStackChartClick && target.isTranspose) {
        comp.onVerticalStackBarChartIconClick(CHART_TYPES.BAR, block);
      } else if (target.isVerticalStackChartClick && !target.isTranspose) {
        comp.onVerticalStackBarChartIconClick(CHART_TYPES.COLUMN, block);
      }
    }

    target.isSignificanceContainerDisabled =
      !target.isSignificanceContainerDisabled;
  }

  static OnHideNAClick(comp: any, block: any = null): void {
    const target = block || comp;
    target.isHideNA = !target.isHideNA;

    if (!target.CrossTabOutPutEntireData || target.CrossTabOutPutEntireData.length === 0) {
      if (target.CrossTabOutPutData && target.CrossTabOutPutData.length > 0) {
        target.CrossTabOutPutEntireData = [...target.CrossTabOutPutData];
      }
    }

    let filteredData = [...(target.CrossTabOutPutEntireData || [])];

    if (!target.isTotal) {
      filteredData = filteredData.filter((x: any) => !x.isTotal);
    }

    if (target.isIndex && target.selectedIndex !== '') {
      target.CrossTabOutPutData = filteredData;
      comp.manipulateIndexData(block);
      filteredData = [...(target.CrossTabOutPutData || [])];
    }

    if (target.isHideNA) {
      filteredData = comp.getHideNAFilteredData(filteredData, !!target.isTranspose, block);
    }

    target.CrossTabOutPutData = filteredData;
    target.OutputdataObject = { ...(target.OutputdataObject || {}), Row: [], Column: [] } as any;
    comp.BindTableOutput(target.CrossTabOutPutData, block);

    if (target.isTranspose) {
      comp.handleOutputdataObjectOnTransposeSelect(block);
    }

    comp.updateChartDisablingFlags(block);

    // Fallback if chart view is disabled or all charts are disabled
    if (!target.isChartView) {
      target.isTableView = true;
      target.isVerticalBarChartClick = false;
      target.isVerticalGroupBarChartClick = false;
      target.isVerticalStackChartClick = false;
      target.isLineChartClick = false;
    } else {
      // Fallback if the currently selected chart becomes disabled
      if (target.isVerticalStackChartClick && target.disableStackChart) {
        target.isVerticalStackChartClick = false;
        target.isVerticalBarChartClick = true;
      }
      if (target.isLineChartClick && target.disableLineChart) {
        target.isLineChartClick = false;
        target.isVerticalBarChartClick = true;
      }

      // Rerender the active chart if in chart view
      if (target.isVerticalGroupBarChartClick || target.isVerticalBarChartClick) {
        comp.VerticalChartClick(target.isTranspose ? CHART_TYPES.BAR : CHART_TYPES.COLUMN, block);
      } else if (target.isVerticalStackChartClick) {
        comp.onVerticalStackBarChartIconClick(target.isTranspose ? CHART_TYPES.BAR : CHART_TYPES.COLUMN, block);
      } else if (target.isLineChartClick) {
        comp.onTrendChartIconClick(block);
      }
    }

    if (!block) {
      comp.crossTabService.setCrossTabOuputData(target.CrossTabOutPutData);
    }
  }

  static getCellValue(comp: any, cell: any, block: any = null): any {
    const target = block || comp;
    if (target.isSampleSize) {
      return cell.answerUnweightedBase;
    }

    if (target.isWeightedLabel) {
      return cell.answerWeightedBase;
    }

    if (target.isWeightedNumerator) {
      return cell.answerWeightedNumerator;
    }

    if (target.isUnWeightedNumerator) {
      return cell.answerUnweightedBase;
    }

    return cell.answerWeightedShare
      ?? cell.answerUnweightedShare
      ?? cell.value
      ?? cell.weightedBase
      ?? cell.totalWeightedShare;
  }

  static getHideNAFilteredData(comp: any, data: any[], isTransposed: boolean = false, block: any = null): any[] {
    if (isTransposed) {
      return comp.getHideNAFilteredByRow(data, block);
    }
    return comp.getHideNAFilteredByColumn(data, block);
  }

  static getHideNAFilteredByColumn(comp: any, data: any[], block: any = null): any[] {
    const columnMap = new Map<string, any[]>();

    data.forEach(item => {
      const colKey = item.colVariableCode + "_" + item.colResponseCode;
      if (!columnMap.has(colKey)) {
        columnMap.set(colKey, []);
      }
      columnMap.get(colKey)!.push(item);
    });

    const validColumns = new Set<string>();

    columnMap.forEach((items, key) => {
      const hasValid = items.some(cell => {
        const val = comp.getCellValue(cell, block);
        return val !== null && val !== 'NA';
      });
      if (hasValid) {
        validColumns.add(key);
      }
    });

    const result = data.filter(item => {
      const colKey = item.colVariableCode + "_" + item.colResponseCode;
      return validColumns.has(colKey);
    });
    return result;
  }

  static getHideNAFilteredByRow(comp: any, data: any[], block: any = null): any[] {
    const rowMap = new Map<string, any[]>();

    data.forEach(item => {
      const rowKey = item.colVariableCode + "_" + item.colResponseCode;
      if (!rowMap.has(rowKey)) {
        rowMap.set(rowKey, []);
      }
      rowMap.get(rowKey)!.push(item);
    });

    const validRows = new Set<string>();

    rowMap.forEach((items, key) => {
      const hasValid = items.some(cell => {
        const val = comp.getCellValue(cell, block);
        return val !== null && val !== 'NA';
      });
      if (hasValid) {
        validRows.add(key);
      }
    });

    const result = data.filter(item => {
      const rowKey = item.colVariableCode + "_" + item.colResponseCode;
      return validRows.has(rowKey);
    });
    return result;
  }
}
